import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Monitor, Trash2 } from "lucide-react";
import { UserCog, Loader2, ShieldCheck, ShieldOff, KeyRound, Copy as CopyIcon } from "lucide-react";
import { toast } from "sonner";

const AdminProfile = () => {
  const { admin } = useOutletContext() || {};
  const [form, setForm] = useState({ current_password: "", new_email: "", new_password: "", confirm_password: "" });
  const [saving, setSaving] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null); // { newEmail, passwordChanged }
  // 2FA state
  const [twoFAStatus, setTwoFAStatus] = useState({ totp_enabled: false, recovery_codes_remaining: 0 });
  const [setupData, setSetupData] = useState(null); // {secret, otpauth_url, qr_data_url}
  const [enableCode, setEnableCode] = useState("");
  const [enrolling, setEnrolling] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [disablePassword, setDisablePassword] = useState("");
  const [trustedDevices, setTrustedDevices] = useState([]);

  const refreshMe = async () => {
    try {
      const r = await axios.get(`${API}/admin/auth/me`);
      setTwoFAStatus({
        totp_enabled: !!r.data.totp_enabled,
        recovery_codes_remaining: r.data.recovery_codes_remaining || 0,
      });
      if (r.data.totp_enabled) {
        const dev = await axios.get(`${API}/admin/auth/trusted-devices`);
        setTrustedDevices(dev.data.devices || []);
      } else {
        setTrustedDevices([]);
      }
    } catch { /* ignore */ }
  };

  useEffect(() => { refreshMe(); /* eslint-disable-next-line */ }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.current_password) {
      toast.error("Enter your current password");
      return;
    }
    if (form.new_password && form.new_password !== form.confirm_password) {
      toast.error("New password and confirmation do not match");
      return;
    }
    if (form.new_password && form.new_password.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    const payload = { current_password: form.current_password };
    const trimmedEmail = form.new_email.trim();
    if (trimmedEmail && trimmedEmail.toLowerCase() !== (admin?.email || "").toLowerCase()) {
      payload.new_email = trimmedEmail;
    }
    if (form.new_password) payload.new_password = form.new_password;
    if (!payload.new_email && !payload.new_password) {
      toast.error("Enter a new email or a new password to change");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/auth/profile`, payload);
      const updated = res.data?.updated || [];
      setSuccessInfo({
        newEmail: updated.includes("email") ? trimmedEmail : (admin?.email || ""),
        emailChanged: updated.includes("email"),
        passwordChanged: updated.includes("password_hash"),
      });
      toast.success("Profile updated — please sign in again");
      // Clear the form so old values disappear
      setForm({ current_password: "", new_email: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  // 2FA actions
  const startSetup = async () => {
    try {
      const res = await axios.post(`${API}/admin/auth/2fa/setup`, {});
      setSetupData(res.data);
      setRecoveryCodes(null);
      setEnableCode("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start 2FA setup");
    }
  };

  const enable2FA = async (e) => {
    e.preventDefault();
    if (!enableCode.trim()) return;
    setEnrolling(true);
    try {
      const res = await axios.post(`${API}/admin/auth/2fa/enable`, { code: enableCode.trim() });
      setRecoveryCodes(res.data.recovery_codes || []);
      setSetupData(null);
      toast.success("Two-factor authentication enabled");
      refreshMe();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not enable 2FA");
    } finally {
      setEnrolling(false);
    }
  };

  const disable2FA = async () => {
    if (!disablePassword) {
      toast.error("Enter your current password to disable 2FA");
      return;
    }
    try {
      await axios.post(`${API}/admin/auth/2fa/disable`, { current_password: disablePassword });
      setDisablePassword("");
      setRecoveryCodes(null);
      toast.success("Two-factor authentication disabled");
      refreshMe();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not disable 2FA");
    }
  };

  const copyRecovery = () => {
    if (!recoveryCodes) return;
    navigator.clipboard.writeText(recoveryCodes.join("\n"));
    toast.success("Recovery codes copied to clipboard");
  };

  const revokeDevice = async (device_id) => {
    try {
      await axios.delete(`${API}/admin/auth/trusted-devices/${device_id}`);
      toast.success("Device revoked");
      refreshMe();
    } catch (e) {
      toast.error("Could not revoke");
    }
  };

  const revokeAllDevices = async () => {
    if (!window.confirm("Revoke ALL trusted devices? Every browser will need 2FA again on next login.")) return;
    try {
      await axios.post(`${API}/admin/auth/trusted-devices/revoke-all`, {});
      toast.success("All devices revoked");
      refreshMe();
    } catch (e) {
      toast.error("Could not revoke all");
    }
  };

  return (
    <div className="space-y-6 max-w-xl" data-testid="admin-profile">
      <div>
        <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
          <UserCog className="w-5 h-5 text-[#002FA7]" /> Admin Profile
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Change your admin email or password. Both fields require your current password.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-zinc-900 border border-zinc-800 rounded p-6 space-y-5">
        <div className="bg-zinc-800/50 border border-zinc-800 rounded px-3 py-2">
          <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Current admin email</p>
          <p className="text-sm text-zinc-200 font-mono mt-0.5" data-testid="profile-current-email">{admin?.email || "—"}</p>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-zinc-400">Current password *</label>
          <input
            type="password"
            value={form.current_password}
            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            required
            autoComplete="current-password"
            className="w-full mt-1 px-3 py-2 bg-black/40 border border-zinc-700 rounded text-zinc-100 focus:border-[#002FA7] focus:outline-none"
            data-testid="profile-current-password"
          />
        </div>

        <div className="border-t border-zinc-800 pt-5 space-y-5">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Change email</p>
          <div>
            <label className="text-xs uppercase tracking-wide text-zinc-400">New email</label>
            <input
              type="email"
              value={form.new_email}
              onChange={(e) => setForm({ ...form, new_email: e.target.value })}
              placeholder="Leave blank to keep current email"
              autoComplete="off"
              className="w-full mt-1 px-3 py-2 bg-black/40 border border-zinc-700 rounded text-zinc-100 focus:border-[#002FA7] focus:outline-none"
              data-testid="profile-new-email"
            />
          </div>
        </div>

        <div className="border-t border-zinc-800 pt-5 space-y-5">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Change password</p>
          <div>
            <label className="text-xs uppercase tracking-wide text-zinc-400">New password</label>
            <input
              type="password"
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              placeholder="Leave blank to keep current password"
              autoComplete="new-password"
              className="w-full mt-1 px-3 py-2 bg-black/40 border border-zinc-700 rounded text-zinc-100 focus:border-[#002FA7] focus:outline-none"
              data-testid="profile-new-password"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-zinc-400">Confirm new password</label>
            <input
              type="password"
              value={form.confirm_password}
              onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
              autoComplete="new-password"
              className="w-full mt-1 px-3 py-2 bg-black/40 border border-zinc-700 rounded text-zinc-100 focus:border-[#002FA7] focus:outline-none"
              data-testid="profile-confirm-password"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={saving || !!successInfo}
          className="w-full py-2.5 bg-[#002FA7] hover:bg-[#00227A] text-white font-medium rounded flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="profile-save-btn"
        >
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save changes"}
        </button>
      </form>

      {successInfo && (
        <div className="bg-emerald-900/25 border border-emerald-700 rounded p-5 space-y-3" data-testid="profile-success-banner">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            <p className="text-base font-semibold text-emerald-200">Your credentials have been updated</p>
          </div>
          <ul className="text-sm text-emerald-100 space-y-1.5 list-disc pl-5">
            {successInfo.emailChanged && (
              <li data-testid="profile-success-email-row">
                Email is now <span className="font-mono bg-black/30 px-1.5 py-0.5 rounded">{successInfo.newEmail}</span>
              </li>
            )}
            {successInfo.passwordChanged && (
              <li data-testid="profile-success-password-row">Password has been changed.</li>
            )}
          </ul>
          <p className="text-xs text-emerald-200/80">
            For security, you must sign in again. <strong>Your browser may auto-fill your old password — clear it and use the new one.</strong>
          </p>
          <button
            onClick={async () => {
              try { await axios.post(`${API}/admin/auth/logout`, {}); } catch { /* ignore */ }
              window.location.href = "/admin/login";
            }}
            className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium rounded inline-flex items-center gap-2"
            data-testid="profile-success-relogin-btn"
          >
            Sign in again
          </button>
        </div>
      )}

      {/* Two-Factor Authentication */}
      <div className="bg-zinc-900 border border-zinc-800 rounded p-6 space-y-5" data-testid="admin-2fa-section">
        <div className="flex items-center gap-2">
          <ShieldCheck className={`w-5 h-5 ${twoFAStatus.totp_enabled ? "text-emerald-400" : "text-zinc-500"}`} />
          <h2 className="text-base font-semibold text-white">Two-Factor Authentication</h2>
          {twoFAStatus.totp_enabled && (
            <span className="text-[10px] uppercase tracking-wide bg-emerald-700 text-white px-1.5 py-0.5 rounded">enabled</span>
          )}
        </div>
        <p className="text-xs text-zinc-400 -mt-2">
          Add a one-time-password challenge to your admin login using Google Authenticator, Authy, 1Password, or any TOTP-compatible app.
        </p>

        {!twoFAStatus.totp_enabled && !setupData && !recoveryCodes && (
          <button
            onClick={startSetup}
            className="px-3 py-2 text-sm bg-[#002FA7] hover:bg-[#00227A] text-white rounded inline-flex items-center gap-2"
            data-testid="admin-2fa-start-btn"
          >
            <ShieldCheck className="w-4 h-4" /> Set up 2FA
          </button>
        )}

        {setupData && (
          <form onSubmit={enable2FA} className="border border-zinc-800 rounded p-4 space-y-4 bg-black/30" data-testid="admin-2fa-setup">
            <div>
              <p className="text-sm text-white font-medium mb-2">1. Scan this QR code with your authenticator app</p>
              <img src={setupData.qr_data_url} alt="2FA QR code" className="bg-white p-2 rounded inline-block" data-testid="admin-2fa-qr" />
              <p className="text-[11px] text-zinc-400 mt-2">
                Or enter this secret manually: <span className="font-mono text-zinc-200 break-all" data-testid="admin-2fa-secret">{setupData.secret}</span>
              </p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-zinc-400">2. Enter the 6-digit code shown in the app</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={enableCode}
                onChange={(e) => setEnableCode(e.target.value.replace(/\D/g, ""))}
                required
                autoFocus
                className="w-full mt-1 px-3 py-2 bg-black/40 border border-zinc-700 rounded text-zinc-100 font-mono tracking-widest"
                placeholder="123456"
                data-testid="admin-2fa-confirm-input"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={enrolling}
                className="px-3 py-2 text-sm bg-emerald-700 hover:bg-emerald-800 text-white rounded inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="admin-2fa-confirm-btn"
              >
                {enrolling ? <><Loader2 className="w-4 h-4 animate-spin" /> Enabling…</> : "Enable 2FA"}
              </button>
              <button
                type="button"
                onClick={() => { setSetupData(null); setEnableCode(""); }}
                className="px-3 py-2 text-sm border border-zinc-700 text-zinc-300 rounded"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {recoveryCodes && (
          <div className="border border-amber-800 bg-amber-900/20 rounded p-4 space-y-3" data-testid="admin-2fa-recovery">
            <div className="flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-amber-400" />
              <p className="text-sm font-semibold text-amber-300">Save these recovery codes</p>
            </div>
            <p className="text-xs text-amber-300">
              Store them somewhere safe. Each one is single-use and lets you log in if you lose access to your authenticator app.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono text-sm text-amber-100">
              {recoveryCodes.map((c) => (
                <span key={c} className="bg-black/40 px-2 py-1 rounded text-center" data-testid={`admin-2fa-code-${c}`}>{c}</span>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={copyRecovery} className="text-xs px-2.5 py-1 border border-amber-700 text-amber-200 hover:text-white rounded inline-flex items-center gap-1.5" data-testid="admin-2fa-copy-codes">
                <CopyIcon className="w-3 h-3" /> Copy all
              </button>
              <button onClick={() => setRecoveryCodes(null)} className="text-xs px-2.5 py-1 text-amber-300 hover:text-white">
                I've saved them
              </button>
            </div>
          </div>
        )}

        {twoFAStatus.totp_enabled && !recoveryCodes && (
          <div className="border border-zinc-800 rounded p-4 space-y-3 bg-black/30" data-testid="admin-2fa-disable">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm text-zinc-300">2FA is active.</p>
              <span className="text-[11px] text-zinc-500">{twoFAStatus.recovery_codes_remaining} recovery codes remaining</span>
            </div>
            <div className="flex gap-2 items-end flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs uppercase tracking-wide text-zinc-400">Current password (to disable)</label>
                <input
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  className="w-full mt-1 px-3 py-2 bg-black/40 border border-zinc-700 rounded text-zinc-100"
                  data-testid="admin-2fa-disable-password"
                />
              </div>
              <button
                onClick={disable2FA}
                className="px-3 py-2 text-sm border border-red-800 text-red-300 hover:bg-red-900/30 rounded inline-flex items-center gap-2"
                data-testid="admin-2fa-disable-btn"
              >
                <ShieldOff className="w-4 h-4" /> Disable 2FA
              </button>
            </div>
          </div>
        )}

        {twoFAStatus.totp_enabled && trustedDevices.length > 0 && (
          <div className="border border-zinc-800 rounded p-4 space-y-3 bg-black/30" data-testid="admin-trusted-devices">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Monitor className="w-4 h-4 text-zinc-400" />
                <p className="text-sm font-semibold text-white">Trusted devices ({trustedDevices.length})</p>
              </div>
              <button
                onClick={revokeAllDevices}
                className="text-xs px-2.5 py-1 border border-red-800 text-red-300 hover:bg-red-900/30 rounded"
                data-testid="admin-revoke-all-devices"
              >
                Revoke all
              </button>
            </div>
            <p className="text-[11px] text-zinc-400">
              These browsers can sign in without entering a 2FA code for 30 days. Revoke any device you no longer recognise.
            </p>
            <div className="divide-y divide-zinc-800 border border-zinc-800 rounded">
              {trustedDevices.map((d) => (
                <div key={d.device_id} className="flex items-center justify-between p-3 gap-3" data-testid={`admin-device-${d.device_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm text-zinc-100 truncate">
                      {d.label}
                      {d.expired && <span className="ml-2 text-[10px] uppercase tracking-wide bg-zinc-700 text-zinc-300 px-1.5 py-0.5 rounded">expired</span>}
                    </p>
                    <p className="text-[11px] text-zinc-500 tabular-nums">
                      Added {new Date(d.created_at).toLocaleDateString()} · Expires {new Date(d.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => revokeDevice(d.device_id)}
                    className="text-zinc-500 hover:text-red-400 p-1.5"
                    title="Revoke this device"
                    data-testid={`admin-revoke-device-${d.device_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProfile;
